import { Skeleton } from 'antd';
import {
  formatCurrency,
  calculateTotal,
  calculateGroupTotal,
  type BilancoHesap,
} from '../utils/calculations';

interface BilancoTableProps {
  title: string;
  subtitle: string;
  data: BilancoHesap[];
  side: 'aktif' | 'pasif';
  onRowClick: (path: string) => void;
  isLoading?: boolean;
}

function BilancoTable({ title, subtitle, data, side, onRowClick, isLoading }: BilancoTableProps) {
  const total = calculateTotal(data);
  const headerBgColor = side === 'aktif' ? '#1e3a5f' : '#6b2c3f';

  const groupTotals: Record<string, number> = {};
  data.filter(item => item.level === 1).forEach(group => {
    groupTotals[group.id] = calculateGroupTotal(data, group.id);
  });

  return (
    <div style={tableStyles.container}>
      <div style={{ ...tableStyles.header, background: headerBgColor }}>
        <div style={tableStyles.headerTitle}>{title}</div>
        <div style={tableStyles.headerSubtitle}>{subtitle}</div>
      </div>

      <div style={tableStyles.tableWrapper}>
        {isLoading ? (
          <div style={{ padding: 20 }}>
            <Skeleton active paragraph={{ rows: 8, width: ['100%', '100%', '80%', '100%', '100%', '60%', '100%', '100%'] }} />
          </div>
        ) : (
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={{ ...tableStyles.th, width: '18%', whiteSpace: 'nowrap' }}>Hesap Kodu</th>
                <th style={{ ...tableStyles.th, width: '52%' }}>Hesap Adı</th>
                <th style={{ ...tableStyles.th, width: '30%', textAlign: 'right' }}>Tutar (₺)</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const isMainGroup = item.level === 1;
                const isSubGroup = item.level === 2;
                const isAccount = item.level === 3;

                return (
                  <tr
                    key={item.id}
                    style={{
                      ...tableStyles.tr,
                      ...(isMainGroup ? tableStyles.mainGroupRow : {}),
                      ...(isSubGroup ? tableStyles.subGroupRow : {}),
                      ...(item.isClickable ? tableStyles.clickableRow : {}),
                    }}
                    onClick={() => item.isClickable && item.navigateTo && onRowClick(item.navigateTo)}
                  >
                    <td
                      style={{
                        ...tableStyles.td,
                        ...tableStyles.kodCell,
                        ...(isMainGroup ? tableStyles.mainGroupText : {}),
                        ...(isSubGroup ? tableStyles.subGroupText : {}),
                      }}
                    >
                      {item.kod}
                    </td>
                    <td
                      style={{
                        ...tableStyles.td,
                        ...(isMainGroup ? tableStyles.mainGroupText : {}),
                        ...(isSubGroup ? tableStyles.subGroupText : {}),
                        ...(isAccount ? tableStyles.accountLabel : {}),
                        ...(item.isClickable ? tableStyles.clickableLabel : {}),
                      }}
                    >
                      {item.label}
                      {item.isClickable && <span style={tableStyles.linkIcon}>→</span>}
                    </td>
                    <td
                      style={{
                        ...tableStyles.td,
                        ...tableStyles.tutarCell,
                        ...(isMainGroup ? { ...tableStyles.mainGroupText, fontWeight: 800 } : {}),
                        ...(isSubGroup ? tableStyles.subGroupText : {}),
                        ...(item.tutar < 0 ? tableStyles.negativeValue : {}),
                      }}
                    >
                      {isMainGroup ? formatCurrency(groupTotals[item.id]) : ''}
                      {isSubGroup ? '' : ''}
                      {isAccount ? formatCurrency(item.tutar) : ''}
                    </td>
                  </tr>
                );
              })}

              <tr style={tableStyles.totalRow}>
                <td style={{ ...tableStyles.td, ...tableStyles.totalLabel }} colSpan={2}>
                  {side === 'aktif' ? 'AKTİF TOPLAMI' : 'PASİF TOPLAMI'}
                </td>
                <td style={{ ...tableStyles.td, ...tableStyles.totalValue }}>
                  {formatCurrency(total)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface BilancoSectionProps {
  aktifData: BilancoHesap[];
  pasifData: BilancoHesap[];
  isLoading: boolean;
  onRowClick: (path: string) => void;
}

export function BilancoSection({ aktifData, pasifData, isLoading, onRowClick }: BilancoSectionProps) {
  const aktifTotal = calculateTotal(aktifData);
  const pasifTotal = calculateTotal(pasifData);
  const fark = aktifTotal - pasifTotal;

  return (
    <>
      {/* Bilanço Denge Göstergesi */}
      <div style={pageStyles.balanceCard}>
        <div style={pageStyles.balanceRow}>
          <div style={pageStyles.balanceItem}>
            <span style={pageStyles.balanceLabel}>Aktif Toplam</span>
            <span style={{ ...pageStyles.balanceValue, color: '#1e3a5f' }}>
              {isLoading ? <Skeleton.Input active size="small" style={{ width: 160 }} /> : `${formatCurrency(aktifTotal)} ₺`}
            </span>
          </div>
          <div style={pageStyles.balanceEquals}>−</div>
          <div style={pageStyles.balanceItem}>
            <span style={pageStyles.balanceLabel}>Pasif Toplam</span>
            <span style={{ ...pageStyles.balanceValue, color: '#6b2c3f' }}>
              {isLoading ? <Skeleton.Input active size="small" style={{ width: 160 }} /> : `${formatCurrency(pasifTotal)} ₺`}
            </span>
          </div>
          {!isLoading && Math.abs(fark) > 0.01 && (
            <>
              <div style={pageStyles.balanceEquals}>→</div>
              <div style={pageStyles.balanceItem}>
                <span style={pageStyles.balanceLabel}>Fark</span>
                <span style={{ ...pageStyles.balanceValue, color: fark > 0 ? '#166534' : '#b91c1c' }}>
                  {formatCurrency(fark)} ₺
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bilanço Tabloları */}
      <div className="bilanco-grid" style={pageStyles.grid}>
        <BilancoTable
          title="AKTİF"
          subtitle="Varlıklar"
          data={aktifData}
          side="aktif"
          onRowClick={onRowClick}
          isLoading={isLoading}
        />
        <BilancoTable
          title="PASİF"
          subtitle="Kaynaklar"
          data={pasifData}
          side="pasif"
          onRowClick={onRowClick}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}

const pageStyles: Record<string, React.CSSProperties> = {
  balanceCard: {
    background: 'var(--color-card-bg)',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 1px 3px var(--shadow-color)',
  },
  balanceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    flexWrap: 'wrap' as const,
  },
  balanceItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  balanceLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
  },
  balanceValue: {
    fontSize: 24,
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
  },
  balanceEquals: {
    fontSize: 28,
    fontWeight: 300,
    color: 'var(--color-text-muted)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
};

const tableStyles: Record<string, React.CSSProperties> = {
  container: {
    background: 'var(--color-card-bg)',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 1px 3px var(--shadow-color)',
  },
  header: {
    padding: '14px 20px',
    color: '#ffffff',
    textAlign: 'center' as const,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '0.1em',
  },
  headerSubtitle: {
    fontSize: 11,
    fontWeight: 500,
    opacity: 0.85,
    marginTop: 2,
    letterSpacing: '0.05em',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '10px 14px',
    textAlign: 'left' as const,
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-text-muted)',
    background: 'var(--color-header-bg)',
    borderBottom: '2px solid var(--color-border)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  tr: {
    borderBottom: '1px solid var(--color-border)',
    transition: 'background 0.15s ease',
  },
  td: {
    padding: '10px 14px',
    fontSize: 13,
    color: 'var(--color-text-primary)',
  },
  kodCell: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap' as const,
  },
  tutarCell: {
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  },
  mainGroupRow: {
    background: 'var(--color-accent-bg)',
  },
  mainGroupText: {
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    fontSize: 13,
  },
  subGroupRow: {
    background: 'var(--color-header-bg)',
  },
  subGroupText: {
    fontWeight: 700,
    color: '#1f2937',
    fontSize: 13,
  },
  accountLabel: {
    paddingLeft: 28,
  },
  clickableRow: {
    cursor: 'pointer',
  },
  clickableLabel: {
    color: '#2563eb',
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationColor: '#93c5fd',
    textUnderlineOffset: '2px',
  },
  linkIcon: {
    marginLeft: 8,
    opacity: 0.7,
    fontSize: 12,
  },
  negativeValue: {
    color: '#b91c1c',
  },
  totalRow: {
    background: 'var(--color-header-bg)',
    borderTop: '2px solid var(--color-border)',
  },
  totalLabel: {
    fontWeight: 800,
    fontSize: 13,
    color: 'var(--color-text-primary)',
  },
  totalValue: {
    textAlign: 'right' as const,
    fontWeight: 800,
    fontSize: 14,
    color: 'var(--color-text-primary)',
  },
};
